import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

const loadApi = () =>
  new Promise((resolve) => {
    if (window.YT?.Player) {
      resolve(window.YT);
      return;
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve(window.YT);
    };
    if (!document.getElementById("yt-iframe-api")) {
      const s = document.createElement("script");
      s.id = "yt-iframe-api";
      s.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(s);
    }
  });

const VARS = { autoplay: 1, controls: 0, rel: 0, playsinline: 1, modestbranding: 1, iv_load_policy: 3, disablekb: 1, fs: 0 };

export const YouTubeLoop = ({ id }) => {
  const host = useRef(null);
  const player = useRef(null);
  const [muted, setMuted] = useState(true);

  const setSound = (on) => {
    const p = player.current;
    if (!p?.unMute) return;
    if (on) p.unMute();
    else p.mute();
    p.playVideo();
    setMuted(!on);
  };

  useEffect(() => {
    let alive = true;
    loadApi().then((YT) => {
      if (!alive || !host.current) return;
      player.current = new YT.Player(host.current, {
        videoId: id,
        width: "100%",
        height: "100%",
        playerVars: { ...VARS, loop: 1, playlist: id },
        events: {
          onReady: (e) => {
            e.target.unMute();
            e.target.playVideo();
            setTimeout(() => {
              if (!alive) return;
              const playing = e.target.getPlayerState() === YT.PlayerState.PLAYING;
              if (!playing) {
                e.target.mute();
                e.target.playVideo();
              }
              setMuted(!playing || e.target.isMuted());
            }, 900);
          },
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.ENDED) {
              e.target.seekTo(0);
              e.target.playVideo();
            }
          },
        },
      });
    });
    return () => {
      alive = false;
      player.current?.destroy?.();
    };
  }, [id]);

  useEffect(() => {
    if (!muted) return undefined;
    const onFirst = (ev) => {
      if (ev.target.closest?.("[data-sound-btn]")) return;
      setSound(true);
    };
    const opts = { once: true, capture: true };
    window.addEventListener("pointerdown", onFirst, opts);
    window.addEventListener("keydown", onFirst, opts);
    return () => {
      window.removeEventListener("pointerdown", onFirst, opts);
      window.removeEventListener("keydown", onFirst, opts);
    };
  }, [muted]);

  return (
    <div className="absolute inset-0 overflow-hidden bg-ink" data-testid="hero-youtube">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[118%] w-[118%] -translate-x-1/2 -translate-y-1/2">
        <div ref={host} className="h-full w-full" />
      </div>
      <button type="button" aria-label={muted ? "Attiva audio" : "Disattiva audio"} className="absolute inset-0 z-10 cursor-pointer" onClick={() => setSound(muted)} data-sound-btn data-testid="hero-video-overlay" />
      <button
        type="button"
        data-sound-btn
        onClick={() => setSound(muted)}
        className="absolute bottom-4 right-4 z-20 inline-flex items-center gap-2 rounded-full border border-gold-light/50 bg-ink/70 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-gold-light backdrop-blur-md transition-colors duration-300 hover:bg-ink/90"
        data-testid="hero-sound-btn"
      >
        {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        {muted ? "Attiva audio" : "Audio attivo"}
      </button>
    </div>
  );
};
